import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Spin, Alert, DatePicker, Button, Space, Empty, Tag, Modal, Checkbox,
  Skeleton, Tooltip, Tabs, Progress, Segmented,
} from 'antd';
import {
  LineChartOutlined, ReloadOutlined, SettingOutlined,
  FundOutlined, TeamOutlined, RiseOutlined, FallOutlined, ThunderboltOutlined,
  TrophyOutlined, DashboardOutlined, AppstoreOutlined, ArrowRightOutlined,
  DatabaseOutlined, ArrowUpOutlined, ArrowDownOutlined, StockOutlined,
  PieChartOutlined, BulbOutlined, FireOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import fundApi from '../api/fundApi';
const { CheckableTag } = Tag;

// ============================================================
// KPI 卡（专业金融风）
// ============================================================
function KpiCard({ icon, label, value, suffix, trend, loading, accent, linkTo, desc, tag }) {
  const accentColor = accent || '#1677ff';
  const content = (
    <Card
      hoverable={!!linkTo}
      className="kpi-card-pro"
      style={{
        background: '#fff',
        borderTop: `3px solid ${accentColor}`,
        height: '100%',
        border: `1px solid ${accentColor}30`,
        borderTopWidth: 3,
      }}
      styles={{ body: { padding: '14px 18px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, letterSpacing: 0.3 }}>
          {label}
          {tag && (
            <Tag color={accentColor} style={{ marginLeft: 6, fontSize: 10, padding: '0 4px', lineHeight: '14px', borderRadius: 3 }}>
              {tag}
            </Tag>
          )}
        </div>
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          background: `${accentColor}15`,
          color: accentColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
        }}>
          {icon}
        </div>
      </div>
      {loading ? (
        <Skeleton.Input active size="default" style={{ width: 90, height: 30, marginTop: 6 }} />
      ) : (
        <div style={{
          fontSize: 28,
          fontWeight: 700,
          color: '#1f2937',
          lineHeight: 1.1,
          marginTop: 8,
          fontFamily: '"SF Mono", Menlo, monospace',
          letterSpacing: -0.5,
        }}>
          {value}
          {suffix && <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 4, fontWeight: 400 }}>{suffix}</span>}
        </div>
      )}
      {(trend !== undefined && trend !== null) && (
        <div style={{
          fontSize: 12,
          marginTop: 4,
          color: trend >= 0 ? '#13a08a' : '#e5484d',
          fontWeight: 500,
        }}>
          {trend >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          <span style={{ marginLeft: 4 }}>
            {trend >= 0 ? '+' : ''}{typeof trend === 'number' ? trend.toFixed(2) : trend}%
          </span>
          {desc && <span style={{ marginLeft: 6, color: '#9ca3af', fontWeight: 400 }}>{desc}</span>}
        </div>
      )}
    </Card>
  );

  if (linkTo) {
    return <Link to={linkTo} style={{ display: 'block', height: '100%', color: 'inherit' }}>{content}</Link>;
  }
  return content;
}

// ============================================================
// 顶部数据条
// ============================================================
function MarketStrip({ stats }) {
  return (
    <div style={{
      background: 'linear-gradient(90deg, #001529 0%, #003a8c 100%)',
      borderRadius: 10,
      padding: '14px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
      color: '#fff',
      flexWrap: 'wrap',
      gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <DashboardOutlined style={{ fontSize: 20, color: '#1890ff' }} />
        <div>
          <div style={{ fontSize: 13, opacity: 0.7 }}>数据总览</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>私募基金数据库</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
        {stats?.latest_nav_date && (
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>最新净值</div>
            <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'monospace' }}>
              {stats.latest_nav_date}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>数据库</div>
          <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.95 }}>
            {stats?.database || 'fund_nav_db'}
          </div>
        </div>
        <Tag color="processing" style={{
          background: 'rgba(82, 196, 26, 0.2)',
          border: '1px solid #52c41a',
          color: '#b7eb8f',
          fontSize: 12,
          padding: '2px 10px',
          borderRadius: 12,
        }}>
          ● 实时同步中
        </Tag>
      </div>
    </div>
  );
}

// ============================================================
// 主组件
// ============================================================
function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indexList, setIndexList] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [dateRange, setDateRange] = useState([dayjs().startOf('year'), dayjs()]);
  const [rangePreset, setRangePreset] = useState('YTD'); // YTD/1M/3M/6M/1Y/3Y/MAX
  const [indexData, setIndexData] = useState({});
  const [strategySummary, setStrategySummary] = useState([]);
  const [strategyReferenceDate, setStrategyReferenceDate] = useState(null);
  const [selectedStrategyKeys, setSelectedStrategyKeys] = useState([]);
  const [strategyConfigVisible, setStrategyConfigVisible] = useState(false);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    await Promise.all([loadStats(), loadIndexList(), loadStrategySummary(), loadDashboardPreferences()]);
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const res = await fundApi.getStatistics();
      if (res.success) setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadDashboardPreferences = async () => {
    try {
      const res = await fundApi.getDashboardPreferences();
      if (res.success && res.data && Array.isArray(res.data.strategy_keys) && res.data.strategy_keys.length > 0) {
        setSelectedStrategyKeys(res.data.strategy_keys);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const DEFAULT_INDEX_CODES = ['000300.SH', '000905.SH', '000852.SH', '000016.SH'];

  // 时间窗口预设
  const RANGE_PRESETS = [
    { key: 'YTD', label: '今年来', compute: () => [dayjs().startOf('year'), dayjs()] },
    { key: '1M', label: '近1月', compute: () => [dayjs().subtract(1, 'month'), dayjs()] },
    { key: '3M', label: '近3月', compute: () => [dayjs().subtract(3, 'month'), dayjs()] },
    { key: '6M', label: '近6月', compute: () => [dayjs().subtract(6, 'month'), dayjs()] },
    { key: '1Y', label: '近1年', compute: () => [dayjs().subtract(1, 'year'), dayjs()] },
    { key: '3Y', label: '近3年', compute: () => [dayjs().subtract(3, 'year'), dayjs()] },
    { key: 'MAX', label: '全部', compute: () => [dayjs('2010-01-01'), dayjs()] },
  ];

  const handlePresetChange = (key) => {
    const preset = RANGE_PRESETS.find((p) => p.key === key);
    if (preset) {
      setRangePreset(key);
      setDateRange(preset.compute());
    }
  };

  const loadIndexList = async () => {
    try {
      const r = await fundApi.getIndexList();
      if (r.success) {
        setIndexList(r.data);
        if (r.data && r.data.length > 0) {
          const def = r.data.filter((idx) => DEFAULT_INDEX_CODES.includes(idx.code)).map((idx) => idx.code);
          setSelectedIndices(def.length > 0 ? def : []);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadStrategySummary = async () => {
    try {
      setLoadingStrategy(true);
      const res = await fundApi.getStrategyPerformanceSummary();
      if (res.success && res.data) {
        setStrategySummary(res.data.strategies || []);
        setStrategyReferenceDate(res.data.reference_date || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStrategy(false);
    }
  };

  const saveStrategyPreferences = async () => {
    try {
      await fundApi.saveDashboardPreferences(selectedStrategyKeys);
    } catch (e) {
      console.error(e);
    }
  };

  const loadIndexData = async () => {
    if (selectedIndices.length === 0) return;
    try {
      setLoading(true);
      setError(null);
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const promises = selectedIndices.map(async (indexCode) => {
        try {
          const data = await fundApi.getIndexData(indexCode, startDate, endDate);
          if (data.success) return { code: indexCode, data: data.data };
          return { code: indexCode, data: null };
        } catch {
          return { code: indexCode, data: null };
        }
      });
      const results = await Promise.all(promises);
      const newIndexData = {};
      results.forEach(({ code, data }) => {
        if (data) newIndexData[code] = data;
      });
      setIndexData(newIndexData);
    } catch (e) {
      console.error(e);
      setError('加载指数数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedIndices.length > 0) loadIndexData();
  }, [selectedIndices, dateRange]);

  const handleIndexToggle = (code) => {
    setSelectedIndices((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const getIndexName = (code) => {
    const index = indexList.find((idx) => idx.code === code);
    return index ? index.name : code;
  };

  const getIndexChartOption = () => {
    if (Object.keys(indexData).length === 0) return null;
    const series = [];
    let dates = [];
    Object.values(indexData).forEach((data) => {
      if (data && data.dates) dates = [...new Set([...dates, ...data.dates])].sort();
    });
    if (dates.length === 0) return null;

    const palette = ['#1677ff', '#13a08a', '#fa8c16', '#722ed1', '#eb2f96', '#52c41a'];
    selectedIndices.forEach((code, idx) => {
      const data = indexData[code];
      if (data && data.dates && data.values) {
        const alignedValues = dates.map((date) => {
          const dateIndex = data.dates.indexOf(date);
          return dateIndex >= 0 ? data.values[dateIndex] : null;
        });
        let startValue = null;
        const returns = alignedValues.map((v) => {
          if (v == null) return null;
          if (startValue == null) {
            startValue = v;
            return 0;
          }
          return ((v - startValue) / startValue) * 100;
        });
        const color = palette[idx % palette.length];
        series.push({
          name: getIndexName(code),
          type: 'line',
          data: returns,
          smooth: true,
          symbol: 'none',
          symbolSize: 3,
          lineStyle: { width: 2 },
          itemStyle: { color },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: `${color}22` },
                { offset: 1, color: `${color}00` },
              ],
            },
          },
          emphasis: { focus: 'series', lineStyle: { width: 3 } },
        });
      }
    });

    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => v == null ? '-' : `${v.toFixed(2)}%`,
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#1f2937', fontSize: 12 },
        axisPointer: { type: 'cross', lineStyle: { color: '#9ca3af', type: 'dashed' }, crossStyle: { color: '#9ca3af' } },
      },
      legend: {
        top: 0,
        type: 'scroll',
        icon: 'roundRect',
        itemWidth: 14,
        itemHeight: 8,
        textStyle: { fontSize: 12, color: '#374151' },
      },
      grid: { left: 50, right: 16, top: 36, bottom: 28 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        axisLabel: {
          formatter: (v) => dayjs(v).format('MM-DD'),
          interval: Math.floor(dates.length / 8),
          color: '#9ca3af',
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
        axisLabel: {
          formatter: (v) => `${v.toFixed(1)}%`,
          color: '#9ca3af',
          fontSize: 11,
        },
      },
      series,
    };
  };

  // 计算每个指数在当前时间窗内的累计收益
  const getIndexPerformanceTable = () => {
    const palette = ['#1677ff', '#13a08a', '#fa8c16', '#722ed1', '#eb2f96', '#52c41a'];
    const result = [];
    selectedIndices.forEach((code, idx) => {
      const data = indexData[code];
      if (!data || !data.values || data.values.length === 0) return;
      const first = data.values.find((v) => v != null);
      const last = [...data.values].reverse().find((v) => v != null);
      if (first == null || last == null) return;
      const changePct = ((last - first) / first) * 100;
      result.push({
        code,
        name: getIndexName(code),
        changePct,
        color: palette[idx % palette.length],
      });
    });
    return result;
  };

  // 按 YTD 中位数排序的策略
  const sortedStrategies = [...strategySummary].sort((a, b) => {
    const va = a.return_ytd_median ?? a.return_ytd_avg ?? -Infinity;
    const vb = b.return_ytd_median ?? b.return_ytd_avg ?? -Infinity;
    return vb - va;
  });
  const topGainers = sortedStrategies.slice(0, 6);
  const topLosers = [...sortedStrategies].reverse().slice(0, 6);

  // ============================================================
  // 渲染
  // ============================================================
  return (
    <div>
      <MarketStrip stats={stats} />

      {/* ========== 1. 顶部数据条 (3 卡) ========== */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={8}>
          <KpiCard
            icon={<FundOutlined />}
            label="覆盖基金"
            value={(stats?.total_funds || 0).toLocaleString()}
            suffix="只"
            accent="#1677ff"
            loading={loadingStats}
            linkTo="/funds"
            tag="FUNDS"
            desc={`点击查看列表`}
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <KpiCard
            icon={<StockOutlined />}
            label="业绩覆盖"
            value={(stats?.funds_with_performance || 0).toLocaleString()}
            suffix="只"
            accent="#13a08a"
            loading={loadingStats}
            linkTo="/funds"
            tag="PERF"
            desc="有业绩指标"
          />
        </Col>
        <Col xs={12} sm={12} md={8}>
          <KpiCard
            icon={<DatabaseOutlined />}
            label="净值记录"
            value={(stats?.total_nav_records || 0).toLocaleString()}
            suffix="条"
            accent="#722ed1"
            loading={loadingStats}
            tag="RECORDS"
          />
        </Col>
      </Row>

      {/* ========== 2. 策略表现 Tab ========== */}
      <Card
        title={
          <Space size={8}>
            <FireOutlined style={{ color: '#fa8c16' }} />
            <span style={{ fontWeight: 600 }}>策略表现</span>
            <Tag color="blue" style={{ marginLeft: 4 }}>
              平均 + 中位数
            </Tag>
          </Space>
        }
        extra={
          <Space>
            {strategyReferenceDate && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>截点 {strategyReferenceDate}</span>
            )}
            <Button type="text" icon={<SettingOutlined />} onClick={() => setStrategyConfigVisible(true)} />
            <Link to="/investment-analysis" style={{ color: '#1677ff', fontSize: 13 }}>
              完整分析 <ArrowRightOutlined />
            </Link>
          </Space>
        }
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: '0' } }}
      >
        <Tabs
          defaultActiveKey="gainers"
          size="small"
          style={{ padding: '0 20px' }}
          items={[
            {
              key: 'gainers',
              label: <span><RiseOutlined style={{ color: '#e5484d' }} /> YTD 领涨</span>,
              children: (
                <StrategyGrid
                  loading={loadingStrategy}
                  list={topGainers}
                />
              ),
            },
            {
              key: 'losers',
              label: <span><FallOutlined style={{ color: '#13a08a' }} /> YTD 领跌</span>,
              children: (
                <StrategyGrid
                  loading={loadingStrategy}
                  list={topLosers}
                />
              ),
            },
            {
              key: 'mine',
              label: <span><BulbOutlined style={{ color: '#1677ff' }} /> 自选观察</span>,
              children: (
                <StrategyGrid
                  loading={loadingStrategy}
                  list={strategySummary.filter((s) => selectedStrategyKeys.includes(s.strategy_level2))}
                  emptyText="未配置自选策略，请到设置中选择"
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="选择自选策略"
        open={strategyConfigVisible}
        onCancel={() => { saveStrategyPreferences(); setStrategyConfigVisible(false); }}
        footer={null}
        width={400}
      >
        <div style={{ marginBottom: 8, color: '#6b7280' }}>勾选要长期观察的二级策略。</div>
        <Checkbox.Group
          value={selectedStrategyKeys}
          onChange={(vals) => setSelectedStrategyKeys(vals || [])}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {strategySummary.map((s) => (
            <Checkbox key={s.strategy_level2} value={s.strategy_level2}>
              {s.strategy_level2}（{s.fund_count} 只）
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Modal>

      {/* ========== 2.5 各类策略业绩指标（新模块） ========== */}
      <StrategyOverviewCard />

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {/* ========== 3. 指数走势 ========== */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space size={8}>
                <LineChartOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 600 }}>主要指数走势</span>
                <Tag color="purple" style={{ marginLeft: 4 }}>累计收益 %</Tag>
              </Space>
            }
            extra={
              <Tooltip title="刷新">
                <Button type="text" icon={<ReloadOutlined />} onClick={loadIndexData} loading={loading} />
              </Tooltip>
            }
            style={{ height: '100%' }}
            styles={{ body: { padding: '14px 18px' } }}
          >
            {/* 时间窗口快捷 */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              <Segmented
                size="small"
                value={rangePreset}
                onChange={handlePresetChange}
                options={RANGE_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
              />
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>
                {dateRange[0].format('YYYY-MM-DD')} ~ {dateRange[1].format('YYYY-MM-DD')}
              </span>
            </div>

            {/* 指数勾选 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {indexList.slice(0, 8).map((idx) => (
                <CheckableTag
                  key={idx.code}
                  checked={selectedIndices.includes(idx.code)}
                  onChange={() => handleIndexToggle(idx.code)}
                  style={{ padding: '2px 10px', fontSize: 12, margin: 0 }}
                >
                  {idx.name}
                </CheckableTag>
              ))}
            </div>

            {loading ? (
              <Skeleton active style={{ padding: 24 }} />
            ) : error ? (
              <Alert message={error} type="error" />
            ) : getIndexChartOption() ? (
              <ReactECharts option={getIndexChartOption()} style={{ height: 320 }} notMerge lazyUpdate />
            ) : (
              <Empty description="暂无指数数据" style={{ padding: '40px 0' }} />
            )}

            {/* 指数实时收益对照表 */}
            {getIndexPerformanceTable().length > 0 && (
              <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, letterSpacing: 0.3 }}>
                  当前收益一览（{rangePreset}）
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {getIndexPerformanceTable().map((row) => (
                    <div key={row.code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: row.color, display: 'inline-block' }} />
                      <span style={{ color: '#374151', fontWeight: 500 }}>{row.name}</span>
                      <span
                        style={{
                          color: row.changePct > 0 ? '#e5484d' : row.changePct < 0 ? '#13a08a' : '#6b7280',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {row.changePct > 0 ? '+' : ''}{row.changePct.toFixed(2)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* ========== 4. 快捷入口 + 数据健康 ========== */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space size={8}>
                <ThunderboltOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600 }}>快捷操作</span>
              </Space>
            }
            style={{ height: '100%' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <Row gutter={[8, 8]}>
              {[
                { icon: <FundOutlined />, label: '基金', path: '/funds', color: '#1677ff' },
                { icon: <TeamOutlined />, label: '管理人', path: '/managers', color: '#13a08a' },
                { icon: <StockOutlined />, label: '对比', path: '/compare', color: '#722ed1' },
                { icon: <AppstoreOutlined />, label: '组合', path: '/portfolio', color: '#eb2f96' },
                { icon: <TrophyOutlined />, label: '回顾', path: '/weekly-review', color: '#fa8c16' },
                { icon: <BulbOutlined />, label: '分析', path: '/investment-analysis', color: '#52c41a' },
              ].map((item) => (
                <Col xs={12} sm={8} key={item.path}>
                  <Link to={item.path}>
                    <Card
                      hoverable
                      styles={{ body: { padding: '10px 8px' } }}
                      style={{
                        textAlign: 'center',
                        background: `linear-gradient(180deg, ${item.color}10 0%, #ffffff 100%)`,
                        borderColor: `${item.color}40`,
                        borderWidth: 1,
                      }}
                    >
                      <div style={{ color: item.color, fontSize: 18 }}>{item.icon}</div>
                      <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginTop: 2 }}>{item.label}</div>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>

            <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>📊 数据健康度</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>业绩指标完整</span>
                <span style={{ fontSize: 12, color: '#1677ff', fontWeight: 600, fontFamily: 'monospace' }}>
                  {stats?.total_funds > 0
                    ? `${Math.round(((stats?.funds_with_performance || 0) / stats.total_funds) * 100)}%`
                    : '—'}
                </span>
              </div>
              <Progress
                percent={stats?.total_funds > 0 ? Math.round(((stats?.funds_with_performance || 0) / stats.total_funds) * 100) : 0}
                strokeColor={{ '0%': '#1677ff', '100%': '#13a08a' }}
                size="small"
                showInfo={false}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// ============================================================
// 策略网格（列表型）
// ============================================================
function StrategyGrid({ loading, list, emptyText }) {
  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton.Input key={i} active size="small" style={{ width: '100%', height: 32, marginBottom: 8 }} />
        ))}
      </div>
    );
  }
  if (!list || list.length === 0) {
    return <Empty description={emptyText || '暂无数据'} style={{ padding: '24px 0' }} />;
  }

  const fmt = (v) => v == null ? <span style={{ color: '#cbd5e0' }}>—</span>
    : <span style={{
        color: v > 0 ? '#e5484d' : v < 0 ? '#13a08a' : '#6b7280',
        fontFamily: '"SF Mono", Menlo, monospace',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {v > 0 ? '+' : ''}{Number(v).toFixed(2)}
      </span>;

  return (
    <div style={{ padding: '8px 0 4px' }}>
      {/* 表头 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(140px, 1.4fr) 80px 1fr 1fr 1fr 1fr 1fr',
          gap: 12,
          padding: '8px 16px',
          fontSize: 11,
          color: '#9ca3af',
          fontWeight: 600,
          letterSpacing: 0.3,
          background: '#f8fafc',
          borderRadius: 4,
        }}
      >
        <div>策略</div>
        <div style={{ textAlign: 'right' }}>基金数</div>
        <div style={{ textAlign: 'right' }}>YTD 平均</div>
        <div style={{ textAlign: 'right' }}>YTD 中位数</div>
        <div style={{ textAlign: 'right' }}>近1月</div>
        <div style={{ textAlign: 'right' }}>近3月</div>
        <div style={{ textAlign: 'right' }}>近1年</div>
      </div>

      {/* 行 */}
      <div>
        {list.map((s, idx) => (
          <div
            key={s.strategy_level2}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(140px, 1.4fr) 80px 1fr 1fr 1fr 1fr 1fr',
              gap: 12,
              padding: '10px 16px',
              fontSize: 13,
              alignItems: 'center',
              background: idx % 2 === 0 ? '#fff' : '#fafbfc',
              borderBottom: '1px solid #f3f4f6',
              transition: 'background 0.15s',
              cursor: 'default',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f7ff')}
            onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc')}
          >
            <div style={{
              color: '#1f2937',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {s.strategy_level2}
            </div>
            <div style={{
              textAlign: 'right',
              color: '#6b7280',
              fontFamily: '"SF Mono", Menlo, monospace',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 12,
            }}>
              {s.fund_count || 0}
            </div>
            <div style={{ textAlign: 'right', fontSize: 14, fontWeight: 600 }}>{fmt(s.return_ytd_avg)}%</div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>{fmt(s.return_ytd_median)}%</div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>{fmt(s.return_1m_avg)}%</div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>{fmt(s.return_3m_avg)}%</div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>{fmt(s.return_1y_avg)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 各类策略业绩指标（总览页新模块）
// - 9 个一级策略（CTA / 股票多头 / ...）× 16 个指标列
// - 4 个时间窗口：YTD（默认）/ 近 1 年 / 近 1 月 / 近 1 周
// ============================================================
const OVERVIEW_PRESETS = [
  { key: 'YTD', label: '今年来' },
  { key: '1Y', label: '近 1 年' },
  { key: '1M', label: '近 1 月' },
  { key: '1W', label: '近 1 周' },
];

function fmtPct(v, digits = 2) {
  if (v == null) return <span style={{ color: '#cbd5e0' }}>—</span>;
  return (
    <span style={{
      color: v > 0 ? '#e5484d' : v < 0 ? '#13a08a' : '#6b7280',
      fontFamily: '"SF Mono", Menlo, monospace',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {v > 0 ? '+' : ''}{Number(v).toFixed(digits)}%
    </span>
  );
}
function fmtNum(v, digits = 2) {
  if (v == null) return <span style={{ color: '#cbd5e0' }}>—</span>;
  const num = Number(v);
  return (
    <span style={{
      color: num > 0 ? '#e5484d' : num < 0 ? '#13a08a' : '#6b7280',
      fontFamily: '"SF Mono", Menlo, monospace',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {num > 0 ? '+' : ''}{num.toFixed(digits)}
    </span>
  );
}
function fmtPlain(v, digits = 2) {
  if (v == null) return <span style={{ color: '#cbd5e0' }}>—</span>;
  return (
    <span style={{
      color: '#374151',
      fontFamily: '"SF Mono", Menlo, monospace',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {Number(v).toFixed(digits)}
    </span>
  );
}

function StrategyOverviewTable({ loading, list, windowKey }) {
  // 默认全部折叠
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (l1) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(l1)) next.delete(l1);
      else next.add(l1);
      return next;
    });
  };

  // 表头定义（按图片顺序）
  const cols = [
    {
      key: 'strategy_level1',
      label: '策略',
      w: 'minmax(140px,1.3fr)',
      align: 'left',
      render: (s, opts = {}) => {
        const subs = s.sub_strategies || [];
        const isOpen = expanded.has(s.strategy_level1);
        const indent = opts.indent || 0;
        const isSub = opts.sub;
        if (isSub) {
          return (
            <span style={{ color: '#6b7280', fontSize: 12, paddingLeft: 24 + indent }}>
              └ {s.strategy_level2}
            </span>
          );
        }
        return (
          <span
            style={{
              fontWeight: 500,
              color: '#1f2937',
              cursor: subs.length > 0 ? 'pointer' : 'default',
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            onClick={() => subs.length > 0 && toggle(s.strategy_level1)}
            title={subs.length > 0 ? (isOpen ? '点击折叠二级' : '点击展开二级') : ''}
          >
            <span style={{
              display: 'inline-block',
              width: 14,
              color: '#9ca3af',
              fontSize: 10,
              transition: 'transform 0.15s',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}>
              {subs.length > 0 ? '▶' : ''}
            </span>
            {s.strategy_level1}
            {subs.length > 0 && (
              <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 400 }}>
                ({subs.length})
              </span>
            )}
          </span>
        );
      },
    },
    { key: 'fund_count',         label: '基金数',   w: '70px',  align: 'right', render: (s, opts) => opts && opts.sub ? <span style={{ color: '#6b7280', fontSize: 12 }}>{s.fund_count}</span> : <span style={{ color: '#6b7280', fontSize: 12 }}>{s.fund_count}</span> },
    { key: 'positive_fund_ratio',label: '上涨占比', w: '80px',  align: 'right', render: (s) => fmtPct(s.positive_fund_ratio, 1) },
    { key: 'return_avg',         label: windowKey === 'YTD' ? 'YTD 均' : (windowKey === '1Y' ? '1Y 均' : (windowKey === '1M' ? '1M 均' : '1W 均')), w: '1fr', align: 'right', render: (s) => fmtPct(s.return_avg) },
    { key: 'return_median',      label: '中位数',   w: '1fr',   align: 'right', render: (s) => fmtPct(s.return_median) },
    { key: 'return_max',         label: '最大',     w: '1fr',   align: 'right', render: (s) => fmtPct(s.return_max) },
    { key: 'return_min',         label: '最小',     w: '1fr',   align: 'right', render: (s) => fmtPct(s.return_min) },
    { key: 'ann_return_avg',     label: '年化收益', w: '1fr',   align: 'right', render: (s) => fmtPct(s.ann_return_avg) },
    { key: 'volatility_avg',     label: '波动率',   w: '1fr',   align: 'right', render: (s) => fmtPct(s.volatility_avg) },
    { key: 'max_drawdown_avg',   label: '最大回撤', w: '1fr',   align: 'right', render: (s) => fmtPct(s.max_drawdown_avg) },
    { key: 'sharpe_avg',         label: 'Sharpe',   w: '1fr',   align: 'right', render: (s) => fmtNum(s.sharpe_avg, 3) },
    { key: 'sortino_avg',        label: 'Sortino',  w: '1fr',   align: 'right', render: (s) => fmtNum(s.sortino_avg, 3) },
    { key: 'calmar_avg',         label: 'Calmar',   w: '1fr',   align: 'right', render: (s) => fmtNum(s.calmar_avg, 3) },
    { key: 'win_rate_avg',       label: '日胜率',   w: '1fr',   align: 'right', render: (s) => fmtPct(s.win_rate_avg, 1) },
  ];

  const gridCols = cols.map((c) => c.w).join(' ');

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
          <Skeleton.Input key={i} active size="small" style={{ width: '100%', height: 30, marginBottom: 6 }} />
        ))}
      </div>
    );
  }
  if (!list || list.length === 0) {
    return <Empty description="暂无数据" style={{ padding: '24px 0' }} />;
  }

  return (
    <div style={{ padding: '0 0 8px' }}>
      {/* 表头 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: 8,
          padding: '8px 16px',
          fontSize: 11,
          color: '#9ca3af',
          fontWeight: 600,
          letterSpacing: 0.3,
          background: '#f8fafc',
          borderRadius: 4,
        }}
      >
        {cols.map((c) => (
          <div key={c.key} style={{ textAlign: c.align }}>{c.label}</div>
        ))}
      </div>

      {/* 行 */}
      <div>
        {list.map((s, idx) => {
          const isOpen = expanded.has(s.strategy_level1);
          const subs = s.sub_strategies || [];
          return (
            <React.Fragment key={s.strategy_level1}>
              {/* 一级行 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: 8,
                  padding: '10px 16px',
                  fontSize: 13,
                  alignItems: 'center',
                  background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f7ff')}
                onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc')}
              >
                {cols.map((c) => (
                  <div key={c.key} style={{ textAlign: c.align }}>{c.render(s)}</div>
                ))}
              </div>

              {/* 二级行（展开后渲染） */}
              {isOpen && subs.map((sub, subIdx) => (
                <div
                  key={`${s.strategy_level1}::${sub.strategy_level2}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    gap: 8,
                    padding: '7px 16px',
                    fontSize: 12,
                    alignItems: 'center',
                    background: subIdx % 2 === 0 ? '#f9fafb' : '#f3f4f6',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  {cols.map((c) => (
                    <div key={c.key} style={{ textAlign: c.align }}>{c.render(sub, { sub: true })}</div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function StrategyOverviewCard() {
  const [windowKey, setWindowKey] = useState('YTD');
  const [data, setData] = useState([]);
  const [refDate, setRefDate] = useState(null);
  const [windowStart, setWindowStart] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (w) => {
    setLoading(true);
    try {
      const res = await fundApi.getStrategyLevel1Overview({ window: w });
      if (res.success && res.data) {
        setData(res.data.strategies || []);
        setRefDate(res.data.reference_date || null);
        setWindowStart(res.data.window_start || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(windowKey); }, [windowKey]);

  return (
    <Card
      title={
        <Space size={8}>
          <AppstoreOutlined style={{ color: '#722ed1' }} />
          <span style={{ fontWeight: 600 }}>各类策略业绩指标</span>
          <Tag color="purple" style={{ marginLeft: 4 }}>9 类一级策略</Tag>
          {refDate && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              截点 {refDate}{windowStart && windowStart !== refDate && `（${windowStart} 起）`}
            </span>
          )}
        </Space>
      }
      extra={
        <Segmented
          size="small"
          value={windowKey}
          onChange={(v) => setWindowKey(v)}
          options={OVERVIEW_PRESETS.map((p) => ({ label: p.label, value: p.key }))}
        />
      }
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: '4px 0' } }}
    >
      <div style={{ fontSize: 11, color: '#9ca3af', padding: '0 16px 6px' }}>
        按一级策略分组汇总：收益（含均/中位/最大/最小）、风险（年化/回撤/波动率）、风险调整收益（Sharpe / Sortino / Calmar）。
        仅统计「窗口内有完整起点 + 终点净值」的基金。
      </div>
      <StrategyOverviewTable loading={loading} list={data} windowKey={windowKey} />
    </Card>
  );
}

export default Dashboard;
